import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Avatar
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  Receipt,
  Warning,
  CheckCircle,
  Search,
  FilterList,
  PictureAsPdf,
  TableChart,
  Clear
} from '@mui/icons-material';
import HttpsIcon from '@mui/icons-material/Https';

import { DatePicker, Input, message, ConfigProvider } from 'antd';

import esES from 'antd/es/locale/es_ES';
import moment from 'moment';
import 'moment/locale/es';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts';
import { exportInvoiceReportToPdf } from '../utils/PdfExport';
import { exportInvoicesToExcel } from '../utils/ExcelExport';
import LoadingInformation from '../../Components/LoadingInformation';

moment.locale('es');

function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '0.00';
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    return '0.00';
  }
  return numValue.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

const COLORS = ['#f759ab', '#4b0082', '#7c3aed', '#22c55e', '#f97316', '#06b6d4'];

function InvoiceReport() {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('todos');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [permissionError, setPermissionError] = useState(false);

  // Summary metrics
  const [summary, setSummary] = useState({
    totalInvoiced: 0,
    totalPaid: 0,
    totalPending: 0,
    invoiceCount: 0,
    averageTicket: 0,
    collectionRate: 0
  });

  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  // Configurar las fechas permitidas (solo del mes actual)
  const disabledDate = (current) => {
    if (!current) return false;

    const today = moment().startOf('day');
    const startOfMonth = moment().startOf('month');

    // Deshabilitar fechas fuera del mes actual o fechas futuras
    return current.isBefore(startOfMonth, 'day') ||
      current.isAfter(today, 'day') ||
      !current.isSame(today, 'month');
  };

  const getInvoices = async () => {
    setLoading(true);
    setPermissionError(false);
    try {
      const response = await fetch(`https://dprincess-training.servehttp.com/invoices/annual-invoices-report`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 403) {
        setPermissionError(true);
        setLoading(false);
        return;
      }

      if (response.ok) {
        const data = await response.json();

        // Process invoice data
        for (const invoice of data) {
          let paidAmount = 0;
          let reservationPaid = [];
          for (const details of invoice.invoiceDetails) {
            paidAmount += parseNumber(details.amount);
            if (details.serviceID === 24) {
              details.amount = parseNumber(details.amount);
              reservationPaid.push(details);
            }
          }
          invoice.pendingAmount = parseNumber(invoice.totalAmount) - paidAmount;
          invoice.paidAmount = paidAmount;
          invoice.reservationPaid = reservationPaid;
        }

        setInvoices(data);
        setFilteredInvoices(data);
        calculateSummary(data);
      } else {
        messageApi.error('Error al cargar las facturas');
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar las facturas');
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (invoiceData) => {
    const totalInvoiced = invoiceData.reduce((sum, inv) => sum + parseNumber(inv.totalAmount), 0);
    const totalPaid = invoiceData.reduce((sum, inv) => sum + inv.paidAmount, 0);
    const totalPending = totalInvoiced - totalPaid;
    const invoiceCount = invoiceData.length;
    const averageTicket = invoiceCount > 0 ? totalInvoiced / invoiceCount : 0;
    const collectionRate = totalInvoiced > 0 ? (totalPaid / totalInvoiced) * 100 : 0;

    setSummary({
      totalInvoiced,
      totalPaid,
      totalPending,
      invoiceCount,
      averageTicket,
      collectionRate
    });
  };

  const applyFilters = useCallback(() => {
    let filtered = [...invoices];

    // Filter by payment status
    if (paymentStatus !== 'todos') {
      filtered = filtered.filter(
        invoice => invoice.paymentStatus.toLowerCase() === paymentStatus
      );
    }

    // Filter by search term
    if (search.trim()) {
      const searchTerm = search.toLowerCase();
      filtered = filtered.filter(
        invoice =>
          invoice.invoiceNumber?.toString().toLowerCase().includes(searchTerm) ||
          invoice.clientName?.toLowerCase().includes(searchTerm) ||
          invoice.cellphoneNumber?.toLowerCase().includes(searchTerm)
      );
    }

    // Filter by date range
    if (
      dateRange &&
      dateRange.length === 2 &&
      dateRange[0] && typeof dateRange[0].isValid === 'function' && dateRange[0].isValid() &&
      dateRange[1] && typeof dateRange[1].isValid === 'function' && dateRange[1].isValid()
    ) {
      // Calcula las cadenas de fecha de inicio y fin del filtro UNA VEZ, usando el dateRange del scope de useCallback.
      // .valueOf() se usa para obtener el timestamp, lo que hace la conversión a UTC más robusta.
      const filterStartDateString = moment.utc(dateRange[0].valueOf()).startOf('day').format('YYYY-MM-DD');
      const filterEndDateString = moment.utc(dateRange[1].valueOf()).endOf('day').format('YYYY-MM-DD');
      // Este log te mostrará qué rango se está usando realmente para filtrar.

      filtered = filtered.filter(invoice => {
        if (!invoice.invoiceDate) return false;

        // Formatea la fecha de la factura a YYYY-MM-DD en UTC para la comparación.
        const invoiceDateString = moment.utc(invoice.invoiceDate).format('YYYY-MM-DD');

        // Realiza la comparación de cadenas.
        const isInRange = invoiceDateString >= filterStartDateString && invoiceDateString <= filterEndDateString;

        return isInRange;
      });
    } else if (dateRange && dateRange.length === 2) {
      // Si dateRange existe pero los objetos no son momentos válidos, podría ser la causa del problema.
      console.error("Los objetos en dateRange no son válidos o no tienen el método isValid:", dateRange);
    }

    setFilteredInvoices(filtered);
    calculateSummary(filtered);
  }, [invoices, search, paymentStatus, dateRange]);

  const clearDateFilter = () => {
    setDateRange(null);
  };

  const prepareChartData = () => {
    // Monthly sales data
    const monthlyData = {};
    filteredInvoices.forEach(invoice => {
      const month = moment(invoice.invoiceDate).locale('es').format('MMM YYYY');
      if (!monthlyData[month]) {
        monthlyData[month] = { month, invoiced: 0, paid: 0 };
      }
      monthlyData[month].invoiced += parseNumber(invoice.totalAmount);
      monthlyData[month].paid += invoice.paidAmount;
    });

    // Payment status distribution
    const statusData = [
      {
        name: 'Pagado',
        value: filteredInvoices.filter(inv => inv.paymentStatus === 'pagado').length,
        color: '#22c55e'
      },
      {
        name: 'Pendiente',
        value: filteredInvoices.filter(inv => inv.paymentStatus === 'pendiente').length,
        color: '#f97316'
      }
    ];

    return {
      monthly: Object.values(monthlyData),
      status: statusData
    };
  };

  const handleExportPDF = () => {
    exportInvoiceReportToPdf(filteredInvoices);
  };

  const handleExportExcel = () => {
    exportInvoicesToExcel(filteredInvoices);
  };

  useEffect(() => {
    getInvoices();
  }, []);

  useEffect(() => {
    if (invoices.length > 0) {
      applyFilters();
    }
  }, [search, paymentStatus, dateRange, invoices, applyFilters]);

  const chartData = prepareChartData();

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, index, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#fff" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>);
  };

  if (loading) {
    return <LoadingInformation />;
  }

  // Mostrar mensaje de falta de permisos si corresponde
  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <ConfigProvider locale={esES}>
      <div style={{ background: '#f3e8ff', minHeight: '100vh', padding: '20px 0' }}>
        <Container maxWidth="xl">
          {contextHolder}

          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                color: '#4b0082',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: {
                  xs: '1.5rem',    // h5 size en móviles
                  sm: '2.125rem',  // h2 size en tablets
                  md: '3rem'       // h2 size en desktop
                }
              }}
            >
              <Assessment sx={{ fontSize: { xs: 30, sm: 50 }, color: '#f759ab' }} />
              Reporte de Facturas
            </Typography>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Button
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                variant="contained"
                sx={{ backgroundColor: '#f759ab', '&:hover': { backgroundColor: '#e04897' } }}
              >
                Exportar PDF
              </Button>
              <Button
                startIcon={<TableChart />}
                onClick={handleExportExcel}
                variant="contained"
                sx={{ backgroundColor: '#4b0082', '&:hover': { backgroundColor: '#3730a3' } }}
              >
                Exportar Excel
              </Button>
            </Stack>
          </Box>

          {/* Filters */}
          <Card sx={{ mb: 4, borderRadius: '18px', border: '1.5px solid #f759ab' }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: '#4b0082',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FilterList sx={{ mr: 1, color: '#f759ab' }} />
                Filtros de Búsqueda
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <ConfigProvider theme={{ token: { colorPrimary: '#f759ab' } }}>
                    <Input
                      placeholder="Buscar por número, cliente o celular"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      allowClear
                      size="large"
                      prefix={<Search />}
                    />
                  </ConfigProvider>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <ConfigProvider locale={esES} theme={{ token: { colorPrimary: '#f759ab' } }}>
                      <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        value={dateRange}
                        onChange={setDateRange}
                        disabledDate={disabledDate}
                        format="DD/MM/YYYY"
                        placeholder={['Fecha inicio', 'Fecha fin']}
                        allowClear={true}
                        size="large"
                        ranges={{
                          'Este Mes': [moment().startOf('month'), moment()],
                          'Últimos 7 Días': [moment().subtract(6, 'days').startOf('day'), moment()],
                          'Últimos 15 Días': [moment().subtract(14, 'days').startOf('day'), moment()],
                          'Hoy': [moment().startOf('day'), moment().endOf('day')]
                        }}
                      />
                    </ConfigProvider>
                    {dateRange && (
                      <Tooltip title="Limpiar filtro de fecha">
                        <IconButton
                          onClick={clearDateFilter}
                          size="small"
                          sx={{
                            color: '#f759ab',
                            '&:hover': { backgroundColor: 'rgba(247, 89, 171, 0.1)' }
                          }}
                        >
                          <Clear />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#4b0082' }}>Estado de pago</InputLabel>
                    <Select
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value)}
                      label="Estado de pago"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                      }}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="pagado">Pagado</MenuItem>
                      <MenuItem value="pendiente">Pendiente</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Indicador del filtro activo */}
              {(dateRange || paymentStatus !== 'todos' || search.trim()) && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {dateRange && (
                    <Chip
                      label={`${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`}
                      onDelete={clearDateFilter}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                  {paymentStatus !== 'todos' && (
                    <Chip
                      label={`Estado: ${paymentStatus}`}
                      onDelete={() => setPaymentStatus('todos')}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                  {search.trim() && (
                    <Chip
                      label={`Búsqueda: ${search}`}
                      onDelete={() => setSearch('')}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #22c55e', background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#15803d' }}>
                        {formatCurrency(summary.totalInvoiced)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Facturado {dateRange ? '(Filtrado)' : ''}
                      </Typography>
                    </Box>
                    <Receipt sx={{ fontSize: 40, color: '#22c55e' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #3b82f6', background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1d4ed8' }}>
                        {formatCurrency(summary.totalPaid)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Cobrado {dateRange ? '(Filtrado)' : ''}
                      </Typography>
                    </Box>
                    <CheckCircle sx={{ fontSize: 40, color: '#3b82f6' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #f97316', background: 'linear-gradient(135deg, #fed7aa, #fdba74)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#c2410c' }}>
                        {formatCurrency(summary.totalPending)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pendiente de Cobro {dateRange ? '(Filtrado)' : ''}
                      </Typography>
                    </Box>
                    <Warning sx={{ fontSize: 40, color: '#f97316' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #f759ab', background: 'linear-gradient(135deg, #fce7f3, #fbcfe8)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#be185d' }}>
                        {summary.invoiceCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Facturas {dateRange ? '(Filtrado)' : ''}
                      </Typography>
                    </Box>
                    <TrendingUp sx={{ fontSize: 40, color: '#f759ab' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Additional Metrics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, p: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4b0082', fontWeight: 600 }}>
                    Promedio de Factura {dateRange ? '(Filtrado)' : ''}
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#f759ab', fontWeight: 700 }}>
                    {formatCurrency(summary.averageTicket)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, p: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: '#4b0082', fontWeight: 600 }}>
                    Tasa de Cobro {dateRange ? '(Filtrado)' : ''}
                  </Typography>
                  <Typography variant="h3" sx={{ color: '#22c55e', fontWeight: 700 }}>
                    {summary.collectionRate.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={7}>
              <Card sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Facturación vs Cobros por Mes
                  {dateRange && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Filtrado: {dateRange[0].format('DD/MM/YYYY')} - {dateRange[1].format('DD/MM/YYYY')}
                    </Typography>
                  )}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.monthly}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="invoiced" fill="#f759ab" name="Facturado" />
                    <Bar dataKey="paid" fill="#22c55e" name="Cobrado" />
                  </BarChart>
                </ResponsiveContainer>

                {/* Leyenda explicativa */}
                <Box sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 4,
                  borderTop: '1px solid #e0e7ff',
                  pt: 2,
                  flexWrap: 'wrap'
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Box sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: '#f759ab',
                      borderRadius: 1,
                      border: '2px solid #fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }} />
                    <Typography variant="body2" sx={{
                      fontWeight: 500,
                      color: '#4b0082',
                      fontSize: '0.9rem'
                    }}>
                      Facturado
                    </Typography>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Box sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: '#22c55e',
                      borderRadius: 1,
                      border: '2px solid #fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }} />
                    <Typography variant="body2" sx={{
                      fontWeight: 500,
                      color: '#4b0082',
                      fontSize: '0.9rem'
                    }}>
                      Cobrado
                    </Typography>
                  </Box>
                </Box>

                {/* Información adicional */}
                <Box sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f8f0fc',
                  borderRadius: 2,
                  textAlign: 'center'
                }}>
                  <Typography variant="caption" sx={{
                    color: '#7c3aed',
                    fontStyle: 'italic',
                    display: 'block',
                    lineHeight: 1.4
                  }}>
                    <strong>Facturado:</strong> Total de dinero facturado por mes<br />
                    <strong>Cobrado:</strong> Total de dinero efectivamente cobrado por mes
                  </Typography>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Estado de Pagos
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData.status}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.status.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>

                {/* Leyenda personalizada */}
                <Box sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 3,
                  borderTop: '1px solid #e0e7ff',
                  pt: 2
                }}>
                  {chartData.status.map((entry, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          backgroundColor: entry.color,
                          borderRadius: '50%',
                          border: '2px solid #fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: '#4b0082',
                          fontSize: '0.9rem'
                        }}
                      >
                        {entry.name}: {entry.value} facturas
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Información adicional */}
                <Box sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f8f0fc',
                  borderRadius: 2,
                  textAlign: 'center'
                }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#7c3aed',
                      fontStyle: 'italic',
                      display: 'block',
                      lineHeight: 1.4
                    }}
                  >
                    <strong>Pagado:</strong> Facturas completamente cobradas<br />
                    <strong>Pendiente:</strong> Facturas con saldo por cobrar
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Invoice Table */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                Detalle de Facturas ({filteredInvoices.length} registros)
                {dateRange && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Mostrando facturas del {dateRange[0].format('DD/MM/YYYY')} al {dateRange[1].format('DD/MM/YYYY')}
                  </Typography>
                )}
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f0fc' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Número</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Pagado</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Pendiente</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInvoices
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((invoice, index) => (
                        <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f3e8ff' } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#7c3aed' }}>
                            {invoice.invoiceNumber}
                          </TableCell>
                          <TableCell>
                            {invoice.invoiceDate
                              ? moment(invoice.invoiceDate).format('DD/MM/YYYY')
                              : 'N/A'}
                          </TableCell>
                          <TableCell>{invoice.clientName}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {formatCurrency(parseNumber(invoice.totalAmount))}
                          </TableCell>
                          <TableCell sx={{ color: '#22c55e', fontWeight: 600 }}>
                            {formatCurrency(invoice.paidAmount)}
                          </TableCell>
                          <TableCell sx={{ color: '#f97316', fontWeight: 600 }}>
                            {formatCurrency(invoice.pendingAmount)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.paymentStatus.toUpperCase()}
                              color={invoice.paymentStatus === 'pagado' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Ver detalles">
                              <IconButton
                                onClick={() => navigate(`/billing/invoice/${invoice.invoiceID}`)}
                                sx={{ color: '#f759ab' }}
                              >
                                <Receipt />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredInvoices.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </CardContent>
          </Card>
        </Container>
      </div>
    </ConfigProvider>
  );
}

export default InvoiceReport;